import { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@phosphor-icons/react';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import {
  useGetDiffusedDocuments,
  useGetValidatedDocuments,
  useGetPendingDocuments,
  useGetNotConsultedDocuments,
} from '../services/dashboard.service';
import { useGetDocumentPresignedUrl } from '../services/documents.service';
import {
  ResendDocumentModal,
  type ResendDocumentModalRef,
} from '../components/documents/ResendDocumentModal';
import { FilteredDocumentsTable } from '../components/documents/FilteredDocumentsTable';

type FilterType = 'validated' | 'diffused' | 'pending' | 'not-consulted';

export const FilteredDocumentsPage = () => {
  const { t } = useTranslation();
  const { filter } = useParams<{ filter: FilterType }>();
  const navigate = useNavigate();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const resendDocumentModalRef = useRef<ResendDocumentModalRef>(null);

  const { data: validatedData, isLoading: isLoadingValidated } = useGetValidatedDocuments(
    filter === 'validated' ? (selectedCompanyId ?? '') : '',
  );

  const { data: diffusedData, isLoading: isLoadingDiffused } = useGetDiffusedDocuments(
    filter === 'diffused' ? (selectedCompanyId ?? '') : '',
  );

  const { data: pendingData, isLoading: isLoadingPending } = useGetPendingDocuments(
    filter === 'pending' ? (selectedCompanyId ?? '') : '',
  );

  const { data: notConsultedData, isLoading: isLoadingNotConsulted } = useGetNotConsultedDocuments(
    filter === 'not-consulted' ? (selectedCompanyId ?? '') : '',
  );

  const getPresignedUrlMutation = useGetDocumentPresignedUrl(selectedCompanyId ?? '');

  const getDocuments = () => {
    switch (filter) {
      case 'validated':
        return validatedData?.documents ?? [];
      case 'diffused':
        return diffusedData?.documents ?? [];
      case 'pending':
        return pendingData?.documents ?? [];
      case 'not-consulted':
        return notConsultedData?.documents ?? [];
      default:
        return [];
    }
  };

  const isLoading =
    filter === 'validated'
      ? isLoadingValidated
      : filter === 'diffused'
        ? isLoadingDiffused
        : filter === 'pending'
          ? isLoadingPending
          : isLoadingNotConsulted;

  const getTitle = () => {
    switch (filter) {
      case 'validated':
        return t('filtered_documents.title.validated');
      case 'diffused':
        return t('filtered_documents.title.diffused');
      case 'pending':
        return t('filtered_documents.title.pending');
      case 'not-consulted':
        return t('filtered_documents.title.not_consulted');
      default:
        return '';
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleOpenDocument = async (nodeId: string) => {
    try {
      const result = await getPresignedUrlMutation.mutateAsync(nodeId);
      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
    }
  };

  const handleResendDocument = (documentId: string) => {
    resendDocumentModalRef.current?.open(documentId);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} weight="regular" />
          {t('common.back')}
        </button>
        <h1 className="text-2xl font-display text-primary">{getTitle()}</h1>
      </div>

      <FilteredDocumentsTable
        documents={getDocuments()}
        isLoading={isLoading}
        filter={filter ?? 'diffused'}
        onOpenDocument={handleOpenDocument}
        onResendDocument={handleResendDocument}
      />

      <ResendDocumentModal ref={resendDocumentModalRef} companyId={selectedCompanyId ?? ''} />
    </div>
  );
};
